import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { usersAPI, organizationsAPI, locationsAPI, brandsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Users,
    UserPlus,
    Search,
    MoreVertical,
    Edit2,
    Trash2,
    Building2,
    MapPin,
    Mail,
    Shield,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Eye,
    Key,
    Power,
    Activity,
    Clock,
    UserCog,
    Send,
    Tag,
    X,
    RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { 
    EmptyState, 
    InfoBanner,
    HelpTooltip 
} from '../components/UsabilityComponents';
import { parseUtc } from '../lib/datetime';

// Role configuration with labels, colors, and descriptions
const ROLE_CONFIG = {
    super_admin: {
        label: 'Super Admin',
        color: 'bg-red-100 text-red-700',
        description: 'Full access to all organizations and features',
        icon: Shield
    },
    franchise_admin: {
        label: 'Franchise Admin',
        color: 'bg-purple-100 text-purple-700',
        description: 'View-only access across multiple brands',
        icon: Eye
    },
    client_admin: {
        label: 'Client Admin',
        color: 'bg-blue-100 text-blue-700',
        description: 'Full access to their organization',
        icon: Building2
    },
    location_manager: {
        label: 'Location Manager',
        color: 'bg-emerald-100 text-emerald-700',
        description: 'Access to their assigned location only',
        icon: MapPin
    },
    employee: {
        label: 'Employee',
        color: 'bg-slate-100 dark:bg-slate-700 text-slate-700',
        description: 'View their own performance data',
        icon: Users
    },
    // The automotive sales-desk role. It already existed in the backend
    // (demo + seeded salespeople) but was missing here, so a dealership could
    // not create one — or designate which one is the insurance broker.
    salesperson: {
        label: 'Salesperson',
        color: 'bg-amber-100 text-amber-700',
        description: 'Sales Desk and vehicle leads only — no Command Center',
        icon: Users
    }
};

const AdminUsers = () => {
    const { t } = useTranslation();
    const { user: currentUser, isSuperAdmin, isClientAdmin, isPartner } = useAuth();
    const [users, setUsers] = useState([]);
    const [loadError, setLoadError] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterOrg, setFilterOrg] = useState('all');
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    
    // Permanent delete modal
    const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
    const [permanentDeleteUser, setPermanentDeleteUser] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deletingPermanently, setDeletingPermanently] = useState(false);
    
    // Password reset modal
    const [passwordResetOpen, setPasswordResetOpen] = useState(false);
    const [passwordResetUser, setPasswordResetUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    
    // Activity log modal
    const [activityLogOpen, setActivityLogOpen] = useState(false);
    const [activityUser, setActivityUser] = useState(null);
    const [activityLogs, setActivityLogs] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(false);
    
    // Send credentials modal
    const [sendCredentialsOpen, setSendCredentialsOpen] = useState(false);
    const [credentialsUser, setCredentialsUser] = useState(null);
    const [credentialsPassword, setCredentialsPassword] = useState('');
    const [sendingCredentials, setSendingCredentials] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: '',
        organization_id: '',
        organization_ids: [],  // NEW: Multiple orgs for Client Admin
        location_id: '',
        location_ids: [],  // NEW: property scope (one or many)
        brand_ids: [],  // NEW: Multiple brands for Franchise Admin
        can_use_ads_feature: false,  // Ads permission toggle
        is_insurance_broker: false,  // Insurance broker (salesperson only)
        partner_access: false,  // Partner: sees all, edits only what is ticked
        can_access_demo_mode: false  // v4.7.0 — Master Demo Mode access
    });

    // Get available roles based on current user's role
    const getAvailableRoles = () => {
        if (isSuperAdmin()) {
            return Object.entries(ROLE_CONFIG).map(([value, config]) => ({
                value,
                label: config.label,
                description: config.description
            }));
        }
        // Client Admin can only create Location Manager and Employee
        return [
            { value: 'location_manager', label: 'Location Manager', description: 'Access to a specific location' },
            { value: 'employee', label: 'Employee', description: 'View their own performance' }
        ];
    };

    // Get locations filtered by selected organization(s)
    const getFilteredLocations = () => {
        if (isClientAdmin()) {
            const orgId = currentUser?.organization_id;
            if (!orgId) return [];
            return locations.filter(loc => loc.organization_id === orgId);
        }
        // For multi-org Client Admin, show locations from all selected orgs
        const selectedOrgIds = formData.organization_ids.length > 0 ? formData.organization_ids : 
                              (formData.organization_id ? [formData.organization_id] : []);
        if (selectedOrgIds.length === 0) return [];
        return locations.filter(loc => selectedOrgIds.includes(loc.organization_id));
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersRes, orgsRes, locsRes, brandsRes] = await Promise.all([
                usersAPI.list(),
                organizationsAPI.list().catch(() => ({ data: [] })),
                locationsAPI.list(),
                brandsAPI.list().catch(() => ({ data: [] }))
            ]);
            
            setUsers(usersRes.data || []);
            setOrganizations(sortByName(orgsRes.data || []));
            setLocations(sortByName(locsRes.data || []));
            setBrands(brandsRes.data || []);
            setLoadError(false);
        } catch (error) {
            console.error('Error loading data:', error);
            setLoadError(true);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                name: user.name || '',
                email: user.email || '',
                password: '',
                role: user.role || '',
                organization_id: user.organization_id || '',
                organization_ids: user.organization_ids || [],
                location_id: user.location_id || '',
                location_ids: (user.location_ids && user.location_ids.length > 0)
                    ? user.location_ids
                    : (user.location_id ? [user.location_id] : []),
                brand_ids: user.brand_ids || [],
                can_use_ads_feature: user.can_use_ads_feature || false,
                is_insurance_broker: user.is_insurance_broker || false,
                partner_access: user.partner_access || false,
                can_access_demo_mode: user.can_access_demo_mode || false
            });
        } else {
            setEditingUser(null);
            setFormData({
                name: '',
                email: '',
                password: '',
                role: isClientAdmin() ? 'location_manager' : '',
                organization_id: isClientAdmin() ? currentUser?.organization_id : '',
                organization_ids: [],
                location_id: '',
                location_ids: [],
                brand_ids: [],
                can_use_ads_feature: false,
                is_insurance_broker: false,
                partner_access: false,
                can_access_demo_mode: false
            });
        }
        setIsModalOpen(true);
    };

    // Helper to add/remove org from selection
    const toggleOrgSelection = (orgId) => {
        setFormData(prev => {
            const newIds = prev.organization_ids.includes(orgId)
                ? prev.organization_ids.filter(id => id !== orgId)
                : [...prev.organization_ids, orgId];
            return { ...prev, organization_ids: newIds };
        });
    };

    // Helper to add/remove a property (location) from a manager's scope
    const toggleLocationSelection = (locId) => {
        setFormData(prev => {
            const newIds = prev.location_ids.includes(locId)
                ? prev.location_ids.filter(id => id !== locId)
                : [...prev.location_ids, locId];
            return { ...prev, location_ids: newIds, location_id: newIds[0] || '' };
        });
    };

    // Helper to add/remove brand from selection
    const toggleBrandSelection = (brandId) => {
        setFormData(prev => {
            const newIds = prev.brand_ids.includes(brandId)
                ? prev.brand_ids.filter(id => id !== brandId)
                : [...prev.brand_ids, brandId];
            return { ...prev, brand_ids: newIds };
        });
    };

    const handleSave = async () => {
        // Validation
        if (!formData.name.trim()) {
            toast.error('Name is required');
            return;
        }
        if (!editingUser && !formData.email.trim()) {
            toast.error('Email is required');
            return;
        }
        if (!editingUser && !formData.password) {
            toast.error('Password is required');
            return;
        }
        if (!formData.role) {
            toast.error('Role is required');
            return;
        }
        
        // Role-specific validations
        if (formData.role === 'client_admin') {
            const hasOrgs = formData.organization_ids.length > 0 || formData.organization_id;
            if (!hasOrgs) {
                toast.error('At least one organization is required for Client Admin');
                return;
            }
        }
        if (formData.role === 'franchise_admin' && formData.brand_ids.length === 0) {
            toast.error('At least one brand is required for Franchise Admin');
            return;
        }
        if (formData.role === 'location_manager' && !formData.location_id && formData.location_ids.length === 0) {
            toast.error(t('adminUsers.propertyRequired'));
            return;
        }
        if (formData.role === 'salesperson' && isSuperAdmin() && !formData.organization_id) {
            toast.error(t('adminUsers.orgRequiredSalesperson', 'A salesperson must belong to a dealership'));
            return;
        }

        setSaving(true);
        try {
            if (editingUser) {
                // Update user
                const updateData = {
                    name: formData.name,
                    role: formData.role,
                    organization_id: formData.organization_id || null,
                    organization_ids: formData.organization_ids.length > 0 ? formData.organization_ids : null,
                    location_id: formData.location_id || null,
                    location_ids: formData.location_ids.length > 0 ? formData.location_ids : null,
                    brand_ids: formData.brand_ids.length > 0 ? formData.brand_ids : null,
                    can_use_ads_feature: formData.can_use_ads_feature,
                    is_insurance_broker: formData.role === 'salesperson' ? !!formData.is_insurance_broker : false,
                    partner_access: formData.role === 'super_admin' ? !!formData.partner_access : false,
                    can_access_demo_mode: formData.can_access_demo_mode
                };
                await usersAPI.update(editingUser.id, updateData);
                toast.success('User updated successfully');
                setIsModalOpen(false);
                loadData();
            } else {
                // Create user
                const result = await usersAPI.create({
                    name: formData.name,
                    email: formData.email,
                    password: formData.password,
                    role: formData.role,
                    organization_id: formData.organization_id || null,
                    organization_ids: formData.organization_ids.length > 0 ? formData.organization_ids : null,
                    location_id: formData.location_id || null,
                    location_ids: formData.location_ids.length > 0 ? formData.location_ids : null,
                    brand_ids: formData.brand_ids.length > 0 ? formData.brand_ids : null,
                    can_use_ads_feature: formData.can_use_ads_feature,
                    is_insurance_broker: formData.role === 'salesperson' ? !!formData.is_insurance_broker : false,
                    partner_access: formData.role === 'super_admin' ? !!formData.partner_access : false,
                    can_access_demo_mode: formData.can_access_demo_mode
                });
                toast.success('User created successfully');
                setIsModalOpen(false);
                loadData();
                
                // Prompt to send credentials email
                setCredentialsUser(result.data.user);
                setCredentialsPassword(result.data.generated_password);
                setSendCredentialsOpen(true);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        
        try {
            await usersAPI.delete(userToDelete.id);
            toast.success('User deactivated');
            setDeleteConfirmOpen(false);
            setUserToDelete(null);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to deactivate user');
        }
    };

    // Password reset handler
    const handlePasswordReset = async () => {
        if (!passwordResetUser || !newPassword) return;
        
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        try {
            await usersAPI.resetPassword(passwordResetUser.id, newPassword);
            toast.success('Password reset successfully');
            setPasswordResetOpen(false);
            setPasswordResetUser(null);
            setNewPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        }
    };

    // Send credentials email handler
    const handleSendCredentials = async () => {
        if (!credentialsUser || !credentialsPassword) return;
        
        setSendingCredentials(true);
        try {
            await usersAPI.sendCredentials(credentialsUser.id, credentialsPassword);
            toast.success(`Credentials sent to ${credentialsUser.email}`);
            setSendCredentialsOpen(false);
            setCredentialsUser(null);
            setCredentialsPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send credentials');
        } finally {
            setSendingCredentials(false);
        }
    };

    // Decline sending credentials
    const handleDeclineCredentials = () => {
        setSendCredentialsOpen(false);
        setCredentialsUser(null);
        setCredentialsPassword('');
        toast.info('Credentials not sent. You can resend them later from the user actions menu.');
    };

    // Resend credentials handler (from user actions menu)
    const handleResendCredentials = (user) => {
        setCredentialsUser(user);
        // Generate a temporary password for resend
        const tempPassword = Math.random().toString(36).slice(-8) + 'A1!';
        setCredentialsPassword(tempPassword);
        setPasswordResetUser(user);
        setNewPassword(tempPassword);
        // First reset the password, then show send dialog
        handleResendWithPasswordReset(user, tempPassword);
    };

    const handleResendWithPasswordReset = async (user, tempPassword) => {
        try {
            await usersAPI.resetPassword(user.id, tempPassword);
            setCredentialsUser(user);
            setCredentialsPassword(tempPassword);
            setSendCredentialsOpen(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate new credentials');
        }
    };

    // Toggle user status handler
    const handleToggleStatus = async (user) => {
        try {
            const result = await usersAPI.toggleStatus(user.id);
            toast.success(result.data.message);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change user status');
        }
    };

    // Open permanent delete modal
    const openPermanentDeleteModal = (user) => {
        setPermanentDeleteUser(user);
        setPermanentDeleteOpen(true);
    };

    // Handle permanent delete
    const handlePermanentDelete = async () => {
        if (!permanentDeleteUser) return;

        setDeletingPermanently(true);
        try {
            await usersAPI.permanentDelete(permanentDeleteUser.id);
            toast.success(`User "${permanentDeleteUser.name}" permanently deleted`);
            setPermanentDeleteOpen(false);
            setPermanentDeleteUser(null);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to permanently delete user');
        } finally {
            setDeletingPermanently(false);
        }
    };

    // Load activity log
    const loadActivityLog = async (user) => {
        setActivityUser(user);
        setActivityLogOpen(true);
        setLoadingActivity(true);
        
        try {
            const response = await usersAPI.getActivity(user.id);
            setActivityLogs(response.data.activities || []);
        } catch (error) {
            toast.error('Failed to load activity log');
            setActivityLogs([]);
        } finally {
            setLoadingActivity(false);
        }
    };

    // Format activity action for display
    const formatActivityAction = (action) => {
        const actionMap = {
            'user_created': 'User account created',
            'user_updated': 'User details updated',
            'user_deactivated': 'User deactivated',
            'user_activated': 'User activated',
            'user_suspended': 'User suspended',
            'password_reset': 'Password was reset',
            'login': 'Logged in',
            'logout': 'Logged out',
            'platform_changed': 'Changed card platform',
            'user_permanently_deleted': 'User permanently deleted',
            'credentials_sent': 'Credentials email sent'
        };
        return actionMap[action] || action;
    };

    const confirmDelete = (user) => {
        setUserToDelete(user);
        setDeleteConfirmOpen(true);
    };

    // Get org/location names
    const getOrgName = (orgId) => {
        const org = organizations.find(o => o.id === orgId);
        return org?.name || '—';
    };

    const getLocationName = (locId) => {
        const loc = locations.find(l => l.id === locId);
        return loc?.name || '—';
    };

    // Filter users
    const filteredUsers = users.filter(u => {
        const matchesSearch = 
            u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            u.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = filterRole === 'all' || u.role === filterRole;
        const matchesOrg = filterOrg === 'all' || u.organization_id === filterOrg;
        return matchesSearch && matchesRole && matchesOrg;
    });

    // Check if current user can edit a target user
    const canEditUser = (targetUser) => {
        if (isSuperAdmin()) return true;
        if (isClientAdmin()) {
            // Can only edit users in their org (except other admins)
            if (targetUser.organization_id !== currentUser?.organization_id) return false;
            if (['super_admin', 'client_admin', 'franchise_admin'].includes(targetUser.role)) return false;
            return true;
        }
        return false;
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Users
                        <HelpTooltip text="Manage user accounts and their access permissions. Assign users to organizations and locations to control what data they can see." />
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {isSuperAdmin() 
                            ? 'Manage all users across organizations'
                            : 'Manage team members in your organization'
                        }
                    </p>
                </div>
                {!isPartner() && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button 
                                onClick={() => openModal()}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="add-user-btn"
                            >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Add User
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-sm">
                                {isClientAdmin() 
                                    ? 'Add Location Managers or Employees to your organization'
                                    : 'Create a new user account'
                                }
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                )}
            </div>

            {/* Info Banner for Client Admin */}
            {isClientAdmin() && (
                <InfoBanner type="info" title={t('adminUsers.yourPermissions')}>
                    As a Client Admin, you can create and manage Location Managers and Employees within your organization.
                    Users you create will automatically be assigned to your organization.
                </InfoBanner>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                    data-testid="search-users"
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Select value={filterRole} onValueChange={setFilterRole}>
                                <SelectTrigger className="w-[160px]" data-testid="filter-role">
                                    <Shield className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder={t('adminUsers.role')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Roles</SelectItem>
                                    {Object.entries(ROLE_CONFIG).map(([value, config]) => (
                                        <SelectItem key={value} value={value}>{config.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            {isSuperAdmin() && (
                                <Select value={filterOrg} onValueChange={setFilterOrg}>
                                    <SelectTrigger className="w-[180px]" data-testid="filter-organization">
                                        <Building2 className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder={t('adminUsers.organization')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Organizations</SelectItem>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Users List */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-muted-foreground mt-4">Loading users...</p>
                </div>
            ) : loadError ? (
                <Card data-testid="users-load-error">
                    <CardContent className="py-16">
                        <div className="flex flex-col items-center text-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                            </div>
                            <h3 className="text-lg font-semibold">We couldn't load users.</h3>
                            <p className="text-muted-foreground text-sm">Please try again.</p>
                            <Button
                                onClick={loadData}
                                className="mt-2"
                                data-testid="users-retry-btn"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Retry
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : filteredUsers.length > 0 ? (
                <div className="grid gap-4">
                    {filteredUsers.map(user => {
                        const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.employee;
                        const RoleIcon = roleConfig.icon;
                        
                        return (
                            <Card key={user.id} className="hover:shadow-md transition-shadow" data-testid={`user-card-${user.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-4">
                                            {/* Avatar */}
                                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <span className="text-lg font-semibold text-blue-600">
                                                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                                </span>
                                            </div>
                                            
                                            {/* Info */}
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-semibold text-foreground">{user.name}</h3>
                                                    <Badge className={roleConfig.color}>
                                                        <RoleIcon className="w-3 h-3 mr-1" />
                                                        {roleConfig.label}
                                                    </Badge>
                                                    {!user.is_active && (
                                                        <Badge variant="destructive" className="text-xs">
                                                            Inactive
                                                        </Badge>
                                                    )}
                                                    {(user.feature_permissions || {}).card_adverts && (
                                                        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 text-xs">
                                                            Advertisements
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                    <Mail className="w-3 h-3" />
                                                    {user.email}
                                                </p>
                                                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                                    {user.organization_id && (
                                                        <span className="flex items-center gap-1">
                                                            <Building2 className="w-3 h-3" />
                                                            {getOrgName(user.organization_id)}
                                                        </span>
                                                    )}
                                                    {user.location_id && (
                                                        <span className="flex items-center gap-1">
                                                            <MapPin className="w-3 h-3" />
                                                            {getLocationName(user.location_id)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Actions */}
                                        {canEditUser(user) && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                                                        <MoreVertical className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => openModal(user)}>
                                                        <Edit2 className="w-4 h-4 mr-2" />
                                                        Edit User
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => {
                                                        setPasswordResetUser(user);
                                                        setPasswordResetOpen(true);
                                                    }}>
                                                        <Key className="w-4 h-4 mr-2" />
                                                        Reset Password
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleResendCredentials(user)}>
                                                        <Send className="w-4 h-4 mr-2" />
                                                        Resend Credentials
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                                                        <Power className="w-4 h-4 mr-2" />
                                                        {user.is_active !== false ? 'Suspend User' : 'Activate User'}
                                                    </DropdownMenuItem>
                                                    {isSuperAdmin() && (
                                                        <DropdownMenuItem onClick={() => loadActivityLog(user)}>
                                                            <Activity className="w-4 h-4 mr-2" />
                                                            View Activity Log
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem 
                                                        className="text-amber-600"
                                                        onClick={() => confirmDelete(user)}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Deactivate
                                                    </DropdownMenuItem>
                                                    {isSuperAdmin() && user.role !== 'super_admin' && (
                                                        <DropdownMenuItem 
                                                            className="text-red-600 font-medium"
                                                            onClick={() => openPermanentDeleteModal(user)}
                                                        >
                                                            <AlertTriangle className="w-4 h-4 mr-2" />
                                                            Permanent Delete
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-16">
                        <EmptyState
                            icon={Users}
                            title={searchTerm || filterRole !== 'all' || filterOrg !== 'all' 
                                ? "No users found" 
                                : "No users yet"
                            }
                            description={
                                searchTerm || filterRole !== 'all' || filterOrg !== 'all'
                                    ? "Try adjusting your search or filters"
                                    : "Create your first user to start managing team access"
                            }
                            action={
                                !searchTerm && filterRole === 'all' && filterOrg === 'all' && (
                                    <Button onClick={() => openModal()}>
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Add Your First User
                                    </Button>
                                )
                            }
                        />
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                {/*
                  Viewport-safe sizing (v4.33.1, 2026-02-19):
                  This form is tall (name + email + password + role +
                  optional Advertising Control + Demo Mode Access +
                  Organizations multi-select). On laptops with short
                  viewports the action buttons were pushed below the
                  fold and unreachable. We now cap the dialog at 92vh,
                  scroll the body, and keep the footer pinned at the
                  bottom so Cancel/Save are always one tap away.
                */}
                <DialogContent
                    className="sm:max-w-lg max-h-[92vh] p-0 flex flex-col overflow-hidden"
                >
                    <div className="px-6 pt-6 pb-2 flex-shrink-0">
                        <DialogHeader>
                            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {editingUser ? 'Edit User' : 'Add New User'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingUser 
                                    ? 'Update the user\'s details and permissions'
                                    : isClientAdmin()
                                        ? 'Add a team member to your organization'
                                        : 'Create a new user account with specific permissions'
                                }
                            </DialogDescription>
                        </DialogHeader>

                        <p className="text-xs text-muted-foreground mt-2">
                            Fields marked with <span className="text-red-500">*</span> are required
                        </p>
                    </div>

                    <div className="px-6 pb-4 space-y-4 overflow-y-auto flex-1" data-testid="add-user-form-body">
                        {/* Name */}
                        <div>
                            <Label className="mb-2 block">
                                Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('adminUsers.fullNamePlaceholder')}
                                data-testid="user-name-input"
                                autoComplete="off"
                            />
                        </div>
                        
                        {/* Email (only for new users) */}
                        {!editingUser && (
                            <div>
                                <Label className="mb-2 block">
                                    Email <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="john@restaurant.com"
                                    data-testid="user-email-input"
                                    autoComplete="off"
                                />
                            </div>
                        )}
                        
                        {/* Password (only for new users) */}
                        {!editingUser && (
                            <div>
                                <Label className="mb-2 block">
                                    Password <span className="text-red-500">*</span>
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="text"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder="Enter password (min 6 characters)"
                                        data-testid="user-password-input"
                                        autoComplete="new-password"
                                        className="flex-1"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                                            let password = '';
                                            for (let i = 0; i < 10; i++) {
                                                password += chars.charAt(Math.floor(Math.random() * chars.length));
                                            }
                                            setFormData({ ...formData, password });
                                        }}
                                        data-testid="generate-password-btn"
                                    >
                                        Generate
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">Minimum 6 characters. User will change on first login.</p>
                            </div>
                        )}
                        
                        {/* Role */}
                        <div>
                            <Label className="mb-2 block">
                                Role <span className="text-red-500">*</span>
                                <HelpTooltip text="The role determines what the user can see and do in the platform" />
                            </Label>
                            <Select 
                                value={formData.role} 
                                onValueChange={(value) => setFormData({ 
                                    ...formData, 
                                    role: value,
                                    // Reset location if changing to non-location-specific role
                                    location_id: value === 'location_manager' ? formData.location_id : '',
                                    location_ids: value === 'location_manager' ? formData.location_ids : []
                                })}
                            >
                                <SelectTrigger data-testid="user-role-select">
                                    <SelectValue placeholder={t('adminUsers.selectRole')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {getAvailableRoles().map(role => (
                                        <SelectItem key={role.value} value={role.value}>
                                            <div className="flex flex-col">
                                                <span>{role.label}</span>
                                                <span className="text-xs text-muted-foreground">{role.description}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Insurance broker — only meaningful on a salesperson */}
                        {formData.role === 'salesperson' && (
                            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                                <div>
                                    <Label className="block">{t('adminUsers.insuranceBroker', 'Insurance broker')}</Label>
                                    <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                                        {t('adminUsers.insuranceBrokerHint', 'This person handles insurance quotes. They see insurance conversations only, and nobody else at the dealership can read them.')}
                                    </p>
                                </div>
                                <Switch
                                    checked={!!formData.is_insurance_broker}
                                    onCheckedChange={(v) => setFormData({ ...formData, is_insurance_broker: v })}
                                    data-testid="user-insurance-broker-toggle"
                                />
                            </div>
                        )}
                        
                        {/* Partner — a Super Admin record that sees everything but may
                            only make the edits ticked on the permissions screen. */}
                        {isSuperAdmin() && formData.role === 'super_admin' && (
                            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                                <div>
                                    <Label className="block">{t('adminUsers.partnerAccess', 'Partner')}</Label>
                                    <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                                        {t('adminUsers.partnerAccessHint', 'Sees every organization, but can only make the edits you tick on the permissions screen. Everything else is refused.')}
                                    </p>
                                </div>
                                <Switch
                                    checked={!!formData.partner_access}
                                    onCheckedChange={(v) => setFormData({ ...formData, partner_access: v })}
                                    data-testid="user-partner-access-toggle"
                                />
                            </div>
                        )}

                        {isSuperAdmin() && formData.role === 'client_admin' && (
                            <div>
                                <Label className="mb-2 block">
                                    Organizations <span className="text-red-500">*</span>
                                    <HelpTooltip text="Select one or more organizations this user can manage" />
                                </Label>
                                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-800 dark:bg-slate-900">
                                    {organizations.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">{t('adminUsers.noOrgsAvailable')}</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {organizations.map(org => (
                                                <label 
                                                    key={org.id} 
                                                    className="flex items-center gap-2 p-2 rounded hover:bg-white dark:hover:bg-slate-800 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.organization_ids.includes(org.id)}
                                                        onChange={() => toggleOrgSelection(org.id)}
                                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm">{org.name}</span>
                                                    {org.brand_name && (
                                                        <Badge variant="outline" className="text-xs ml-auto">
                                                            {org.brand_name}
                                                        </Badge>
                                                    )}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {formData.organization_ids.length > 0 && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {formData.organization_ids.length} organization(s) selected
                                    </p>
                                )}
                            </div>
                        )}
                        
                        {/* Brand(s) for Franchise Admin - Multi-select */}
                        {isSuperAdmin() && formData.role === 'franchise_admin' && (
                            <div>
                                <Label className="mb-2 block">
                                    Brands <span className="text-red-500">*</span>
                                    <HelpTooltip text="Select one or more brands this franchise admin can oversee" />
                                </Label>
                                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-800 dark:bg-slate-900">
                                    {brands.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">No brands available. Create brands first.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {brands.map(brand => (
                                                <label 
                                                    key={brand.id} 
                                                    className="flex items-center gap-2 p-2 rounded hover:bg-white dark:hover:bg-slate-800 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.brand_ids.includes(brand.id)}
                                                        onChange={() => toggleBrandSelection(brand.id)}
                                                        className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                                                    />
                                                    <Tag className="w-4 h-4 text-purple-500" />
                                                    <span className="text-sm">{brand.name}</span>
                                                    <Badge variant="outline" className="text-xs ml-auto">
                                                        {brand.organization_count || 0} orgs
                                                    </Badge>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {formData.brand_ids.length > 0 && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {formData.brand_ids.length} brand(s) selected
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Single Organization for Location Manager and Salesperson.
                            A salesperson MUST be scoped to a dealership — the Sales
                            Desk, vehicle leads and the insurance-broker lookup all
                            query by organization_id. */}
                        {isSuperAdmin() && ['location_manager', 'salesperson'].includes(formData.role) && (
                            <div>
                                <Label className="mb-2 block">
                                    Organization <span className="text-red-500">*</span>
                                    <HelpTooltip text="The organization this user belongs to" />
                                </Label>
                                <Select 
                                    value={formData.organization_id} 
                                    onValueChange={(value) => setFormData({ 
                                        ...formData, 
                                        organization_id: value,
                                        location_id: '', // Reset location when org changes
                                        location_ids: [] // Reset properties when org changes
                                    })}
                                >
                                    <SelectTrigger data-testid="user-org-select">
                                        <SelectValue placeholder={t('adminUsers.selectOrganization')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        {/* Properties (for Location Manager role) — one or many */}
                        {formData.role === 'location_manager' && (isClientAdmin() || formData.organization_id) && (
                            <div>
                                <Label className="mb-2 block">
                                    {t('adminUsers.properties')} <span className="text-red-500">*</span>
                                    <HelpTooltip text={t('adminUsers.propertiesHelp')} />
                                </Label>
                                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-900">
                                    {getFilteredLocations().length === 0 ? (
                                        <p className="text-sm text-amber-600">{t('adminUsers.noPropertiesFound')}</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {getFilteredLocations().map(loc => (
                                                <label
                                                    key={loc.id}
                                                    className="flex items-center gap-2 p-2 rounded hover:bg-white dark:hover:bg-slate-800 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        data-testid={`user-property-checkbox-${loc.id}`}
                                                        checked={formData.location_ids.includes(loc.id)}
                                                        onChange={() => toggleLocationSelection(loc.id)}
                                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm">{loc.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {formData.location_ids.length > 0
                                        ? t('adminUsers.propertiesSelected', { count: formData.location_ids.length })
                                        : t('adminUsers.propertiesHint')}
                                </p>
                            </div>
                        )}

                        {/* Advertising Control REMOVED — ad access is granted in ONE place now:
                            Permissions Manager → Card Settings — Advanced → Advertisements
                            (permissions.card_adverts). This toggle wrote a separate user-level
                            boolean that the backend checked while the UI read the granular
                            permission, so the two silently disagreed and a client admin could
                            look switched ON here yet be refused on save. */}

                        {/* Demo Mode Access - Super Admin grants to trusted staff (sales/execs/QA) */}
                        {isSuperAdmin() && formData.role !== 'super_admin' && (
                            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-slate-50 dark:bg-slate-800">
                                <div className="flex-1">
                                    <Label className="text-sm font-medium text-foreground flex items-center gap-2">
                                        Demo Mode Access
                                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-300">
                                            Blue Flame
                                        </span>
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Allow this user to toggle the Blue Flame Steakhouse demo environment from the Operational Base sidebar. Useful for sales, executives, and trusted QA staff.
                                    </p>
                                </div>
                                <Switch
                                    checked={formData.can_access_demo_mode}
                                    onCheckedChange={(checked) => setFormData({ ...formData, can_access_demo_mode: checked })}
                                    data-testid="demo-mode-access-toggle"
                                />
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter className="px-6 py-4 border-t bg-background flex-shrink-0">
                        <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSave} 
                            disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="save-user-btn"
                        >
                            {saving ? 'Saving...' : (editingUser ? 'Save Changes' : 'Create User')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Deactivate User
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to deactivate <strong>{userToDelete?.name}</strong>?
                            They will no longer be able to log in.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={handleDelete}
                            data-testid="confirm-delete-btn"
                        >
                            Deactivate User
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Password Reset Modal */}
            <Dialog open={passwordResetOpen} onOpenChange={setPasswordResetOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="w-5 h-5 text-blue-600" />
                            Reset Password
                        </DialogTitle>
                        <DialogDescription>
                            Set a new password for <strong>{passwordResetUser?.name}</strong> ({passwordResetUser?.email})
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">
                            New Password <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password (min 6 characters)"
                            data-testid="new-password-input"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                            The user will need to use this password to log in. Consider sharing it securely.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setPasswordResetOpen(false);
                            setNewPassword('');
                        }}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handlePasswordReset}
                            disabled={!newPassword || newPassword.length < 6}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="confirm-reset-btn"
                        >
                            Reset Password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Activity Log Modal */}
            <Dialog open={activityLogOpen} onOpenChange={setActivityLogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Activity className="w-5 h-5 text-purple-600" />
                            Activity Log
                        </DialogTitle>
                        <DialogDescription>
                            Activity history for <strong>{activityUser?.name}</strong> ({activityUser?.email})
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto py-4">
                        {loadingActivity ? (
                            <div className="text-center py-8">
                                <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                                <p className="text-muted-foreground mt-2">Loading activity...</p>
                            </div>
                        ) : activityLogs.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>{t('adminUsers.noActivityRecorded')}</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {activityLogs.map((log, idx) => (
                                    <div key={log.id || idx} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                            <Activity className="w-4 h-4 text-purple-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-foreground">
                                                {formatActivityAction(log.action)}
                                            </p>
                                            {log.details && Object.keys(log.details).length > 0 && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {log.details.target_email && `Target: ${log.details.target_email}`}
                                                </p>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {parseUtc(log.timestamp).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActivityLogOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Send Credentials Modal */}
            <Dialog open={sendCredentialsOpen} onOpenChange={setSendCredentialsOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-blue-600" />
                            Send Login Credentials
                        </DialogTitle>
                        <DialogDescription>
                            Send login credentials to <strong>{credentialsUser?.name}</strong> ({credentialsUser?.email})?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-border">
                            <p className="text-sm font-medium text-foreground mb-2">Email will include:</p>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• Portal login URL</li>
                                <li>• Username (email): {credentialsUser?.email}</li>
                                <li>• Temporary password</li>
                                <li>• User&apos;s role and organization</li>
                            </ul>
                            <p className="text-xs text-amber-600 mt-3 font-medium">
                                User will be required to change password on first login.
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={handleDeclineCredentials}
                            disabled={sendingCredentials}
                        >
                            Don&apos;t Send
                        </Button>
                        <Button 
                            onClick={handleSendCredentials}
                            disabled={sendingCredentials}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="confirm-send-credentials-btn"
                        >
                            {sendingCredentials ? 'Sending...' : 'Send Credentials'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Permanent Delete User Modal */}
            <Dialog open={permanentDeleteOpen} onOpenChange={setPermanentDeleteOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Permanent Delete User
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            This action is <strong>irreversible</strong>. The user will be permanently deleted.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {permanentDeleteUser && (
                        <div className="py-4">
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm font-medium text-red-800 mb-2">
                                    You are about to permanently delete:
                                </p>
                                <p className="text-lg font-bold text-red-900">{permanentDeleteUser.name}</p>
                                <p className="text-sm text-red-700">{permanentDeleteUser.email}</p>
                                <p className="text-sm text-red-600 mt-1">Role: {permanentDeleteUser.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                <ul className="text-sm text-red-700 mt-3 space-y-1">
                                    <li>• User account will be deleted</li>
                                    <li>• Activity logs will be removed</li>
                                    <li>• Any assigned cards will be unassigned</li>
                                </ul>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setPermanentDeleteOpen(false);
                                setPermanentDeleteUser(null);
                            }}
                            disabled={deletingPermanently}
                        >
                            No, Cancel
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handlePermanentDelete}
                            disabled={deletingPermanently}
                            data-testid="confirm-permanent-delete-user-btn"
                        >
                            {deletingPermanently ? 'Deleting...' : 'Yes, Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminUsers;
